import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import {
  Crown, Check, Zap, Star, Shield, BarChart3,
  Loader2, ArrowRight, Sparkles, AlertTriangle,
  MessageSquare, Eye, Camera, FileText, Award
} from "lucide-react";

const PLAN_ICONS = {
  free: FileText,
  growth: Zap,
  premium: Crown,
};

const PLAN_COLORS = {
  free: { bg: "bg-slate-50", border: "border-slate-200", accent: "text-slate-600", badge: "bg-slate-100 text-slate-700", btn: "bg-slate-700 hover:bg-slate-800" },
  growth: { bg: "bg-orange-50/50", border: "border-orange-300", accent: "text-orange-600", badge: "bg-orange-100 text-orange-700", btn: "bg-orange-600 hover:bg-orange-700" },
  premium: { bg: "bg-gradient-to-br from-amber-50 to-orange-50", border: "border-amber-400 ring-2 ring-amber-200", accent: "text-amber-600", badge: "bg-amber-100 text-amber-800", btn: "bg-amber-600 hover:bg-amber-700" },
};

const UsageBar = ({ label, current, limit, icon: Icon }) => {
  const unlimited = limit === -1;
  const pct = unlimited ? 15 : Math.min((current / limit) * 100, 100);
  const exceeded = !unlimited && current >= limit;
  const warning = !unlimited && current >= limit * 0.8;

  return (
    <div className="space-y-1.5" data-testid={`usage-bar-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-slate-600 font-medium">
          <Icon className="w-3.5 h-3.5" /> {label}
        </span>
        <span className={`font-semibold text-xs ${exceeded ? "text-red-600" : warning ? "text-amber-600" : "text-slate-700"}`}>
          {current}{unlimited ? "" : ` / ${limit}`}
          {unlimited && <span className="text-emerald-600 ml-1">Unlimited</span>}
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${exceeded ? "bg-red-500" : warning ? "bg-amber-400" : unlimited ? "bg-emerald-400" : "bg-orange-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {exceeded && (
        <p className="text-[10px] text-red-500 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Limit reached — upgrade for more
        </p>
      )}
    </div>
  );
};

const VendorSubscription = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [razorpayKeyId, setRazorpayKeyId] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, currentRes] = await Promise.all([
        api.get("/subscriptions/plans"),
        api.get("/subscriptions/current"),
      ]);
      setPlans(plansRes.data.plans || []);
      setCurrentSub(currentRes.data.subscription);
      setUsage(currentRes.data.usage);
      setLimits(currentRes.data.limits);
      setWarnings(currentRes.data.warnings || []);
      setRazorpayKeyId(currentRes.data.razorpay_key_id || "");
    } catch {
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (planId === "free") return;
    setPaymentLoading(planId);

    try {
      const res = await api.post("/subscriptions/create-order", {
        plan_id: planId,
        billing_cycle: billingCycle,
      });

      const { razorpay_order_id, amount, key_id } = res.data;

      const options = {
        key: key_id || razorpayKeyId,
        amount,
        currency: "INR",
        name: "OEMLinker",
        description: `${res.data.plan_name} Plan — ${billingCycle === "annual" ? "Annual" : "Monthly"}`,
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            const verifyRes = await api.post("/subscriptions/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(verifyRes.data.message || "Subscription activated!");
            fetchData();
          } catch {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: { color: "#ea580c" },
        modal: {
          ondismiss: () => setPaymentLoading(null),
        },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error("Payment gateway not loaded. Please refresh.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setPaymentLoading(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  const currentPlanId = currentSub?.plan_id || "free";

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto py-6 px-4" data-testid="vendor-subscription-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-500" /> Subscription Plans
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Choose a plan that fits your business. Upgrade anytime to unlock more features.
          </p>
        </div>

        {/* Current Plan + Usage */}
        <Card className="mb-8 border-orange-200 bg-orange-50/30" data-testid="current-plan-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-600" />
                Current Plan: <span className="text-orange-600">{currentSub?.plan_name || "Free"}</span>
                {currentSub?.featured_badge && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 font-semibold flex items-center gap-1">
                    <Award className="w-3 h-3" /> Featured
                  </span>
                )}
              </CardTitle>
              {currentSub?.end_date && (
                <span className="text-xs text-slate-500">
                  Valid till {new Date(currentSub.end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {usage && limits && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <UsageBar
                  label="Quotes this month"
                  current={usage.quotes_this_month}
                  limit={limits.quotes_per_month}
                  icon={FileText}
                />
                <UsageBar
                  label="Machine listings"
                  current={usage.machines}
                  limit={limits.machines}
                  icon={BarChart3}
                />
                <UsageBar
                  label="Portfolio photos"
                  current={usage.portfolio_photos}
                  limit={limits.portfolio_photos}
                  icon={Camera}
                />
              </div>
            )}

            {warnings.length > 0 && (
              <div className="mt-4 space-y-2">
                {warnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    {w.message}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center mb-6 gap-3" data-testid="billing-toggle">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              billingCycle === "monthly"
                ? "bg-orange-600 text-white shadow-md"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid="billing-monthly-btn"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              billingCycle === "annual"
                ? "bg-orange-600 text-white shadow-md"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid="billing-annual-btn"
          >
            Annual
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
              billingCycle === "annual" ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
            }`}>
              Save 17%
            </span>
          </button>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plan-cards-grid">
          {plans.map((plan) => {
            const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.free;
            const PlanIcon = PLAN_ICONS[plan.id] || FileText;
            const isCurrent = plan.id === currentPlanId;
            const isDowngrade = ["premium", "growth"].indexOf(currentPlanId) > ["premium", "growth"].indexOf(plan.id);
            const price = billingCycle === "annual" ? plan.annual_monthly_equivalent : plan.monthly_price_display;

            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${colors.bg} ${colors.border} ${
                  plan.id === "premium" ? "md:-mt-2 md:mb-2" : ""
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.id === "premium" && (
                  <div className="absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> MOST POPULAR
                  </div>
                )}

                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.badge}`}>
                      <PlanIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{plan.name}</h3>
                      {isCurrent && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                          Current Plan
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1 mt-3">
                    {price > 0 ? (
                      <>
                        <span className="text-3xl font-extrabold text-slate-900">
                          ₹{price.toLocaleString("en-IN")}
                        </span>
                        <span className="text-sm text-slate-500">/mo</span>
                        {billingCycle === "annual" && (
                          <span className="text-xs text-slate-400 line-through ml-2">
                            ₹{plan.monthly_price_display.toLocaleString("en-IN")}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-3xl font-extrabold text-slate-900">Free</span>
                    )}
                  </div>
                  {billingCycle === "annual" && price > 0 && (
                    <p className="text-[10px] text-emerald-600 font-medium mt-1">
                      Billed ₹{plan.annual_price_display.toLocaleString("en-IN")}/year (2 months free)
                    </p>
                  )}
                </CardHeader>

                <CardContent className="pt-4">
                  {/* Limits summary */}
                  <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-white/60 rounded-lg border border-slate-100">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-900">
                        {plan.limits.quotes_per_month === -1 ? "∞" : plan.limits.quotes_per_month}
                      </p>
                      <p className="text-[10px] text-slate-500">Quotes/mo</p>
                    </div>
                    <div className="text-center border-x border-slate-100">
                      <p className="text-lg font-bold text-slate-900">
                        {plan.limits.machines === -1 ? "∞" : plan.limits.machines}
                      </p>
                      <p className="text-[10px] text-slate-500">Machines</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-900">
                        {plan.limits.portfolio_photos === -1 ? "∞" : plan.limits.portfolio_photos}
                      </p>
                      <p className="text-[10px] text-slate-500">Photos</p>
                    </div>
                  </div>

                  {/* Features list */}
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colors.accent}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  {plan.id === "free" ? (
                    isCurrent ? (
                      <Button disabled className="w-full bg-slate-200 text-slate-500" data-testid="plan-btn-free">
                        Current Plan
                      </Button>
                    ) : (
                      <Button disabled className="w-full bg-slate-200 text-slate-500" data-testid="plan-btn-free">
                        Free Plan
                      </Button>
                    )
                  ) : isCurrent ? (
                    <Button disabled className="w-full bg-emerald-100 text-emerald-700" data-testid={`plan-btn-${plan.id}`}>
                      <Check className="w-4 h-4 mr-2" /> Active
                    </Button>
                  ) : (
                    <Button
                      className={`w-full text-white ${colors.btn}`}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={paymentLoading === plan.id}
                      data-testid={`plan-btn-${plan.id}`}
                    >
                      {paymentLoading === plan.id ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : isDowngrade ? (
                        "Switch Plan"
                      ) : (
                        <><ArrowRight className="w-4 h-4 mr-2" /> {currentPlanId === "free" ? "Upgrade" : "Switch"} to {plan.name}</>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ / Info */}
        <div className="mt-10 max-w-2xl mx-auto text-center">
          <p className="text-xs text-slate-400">
            All plans include access to the RFQ marketplace. Prices are in INR and exclude applicable taxes.
            Subscriptions auto-renew — manage anytime from this page. Need help? Contact us at oemlinker@gmail.com
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VendorSubscription;
